import { keyName, type Analysis } from './analyze';
import { loc } from './geometry';
import { t as $t } from './i18n';
import { formatPlcAddress, parsePlcAddress, PLC_KIND_NAME, plcChannels, plcTagName, type PlcAddress, type PlcChannel } from './plc';
import type { Placed } from './types';

/*
 * PLC assignment list import: a table of addresses and symbol names (a TIA Portal "PLC tags" export, a CODESYS
 * variable list, an EPLAN assignment list or a hand-made sheet) is matched to the PLC channels of the project.
 * A row whose symbol name belongs to a channel gives that channel its address; a row whose address a channel
 * already has gives it the symbol name. Nothing is changed until the caller applies the result.
 */

export interface AssignRow {
  /** row number in the file (1 = header) */
  line: number;
  addr: string;
  parsed: PlcAddress | null;
  name: string;
  comment: string;
  /** card (device tag) the row belongs to, when the list has such a column */
  card: string;
}

export interface AssignList {
  rows: AssignRow[];
  /** the column found for each field: its header text, or its number in a list without a header */
  columns: { addr?: string; name?: string; comment?: string; card?: string };
  /** rows with an address that is not an input or output (markers, data blocks …) */
  skipped: number;
}

const fold = (s: string) =>
  s.toLocaleLowerCase('tr').replace(/ı/g, 'i').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9а-яё]+/g, ' ').trim();

const HEADERS: Record<keyof AssignList['columns'], string[]> = {
  addr: ['logical address', 'address', 'adres', 'plc adresi', 'adresse', 'absolute adresse', 'absolute address', 'operand', 'direccion', 'endereco', 'adresse plc', 'plc address', 'адрес'],
  name: ['name', 'symbol', 'symbol name', 'symbolname', 'symbolic address', 'symbolische adresse', 'sembol', 'sembol adi', 'tag', 'tag name', 'etiket adi', 'variable', 'variablenname', 'nombre', 'nom', 'nome', 'имя'],
  comment: ['comment', 'kommentar', 'aciklama', 'function text', 'funktionstext', 'description', 'beschreibung', 'comentario', 'commentaire', 'comentario', 'комментарий'],
  card: ['device tag', 'bmk', 'betriebsmittelkennzeichen', 'geratekennzeichen', 'kart', 'card', 'plc karti', 'modul', 'module', 'cihaz etiketi'],
};

// CODESYS: "Start AT %IX0.0 : BOOL;" — a declaration in one cell, with an optional comment
const DECL = /^\s*([A-Za-z_]\w*)\s+AT\s+(%[IQ][XW]?\s*\d+(?:\.\d)?)\s*:\s*\w+\s*;?\s*(?:\(\*\s*(.*?)\s*\*\)|\/\/\s*(.*))?$/i;
const isAddr = (s: string) => /^%?[IQEAM][WXBD]?\s*\d+(?:\.\d+)?$/i.test(s.trim()) || DECL.test(s);

/** Reads an assignment list (rows of a sheet or CSV file) into address / name / comment rows. */
export function readAssignmentList(table: string[][]): AssignList {
  const head = (table[0] ?? []).map((h) => fold(String(h ?? '')));
  const col: Partial<Record<keyof AssignList['columns'], number>> = {};
  for (const [k, names] of Object.entries(HEADERS) as [keyof AssignList['columns'], string[]][]) {
    const i = head.findIndex((h, j) => names.includes(h) && !Object.values(col).includes(j));
    if (i >= 0) col[k] = i;
  }
  let first = 1;
  if (col.addr == null) {
    // no header: the address column is the one with addresses in most rows (a CODESYS or plain list)
    first = 0;
    const width = Math.max(0, ...table.map((r) => r.length));
    let best = -1, hits = 0;
    for (let j = 0; j < width; j++) {
      const n = table.filter((r) => isAddr(String(r[j] ?? ''))).length;
      if (n > hits) [best, hits] = [j, n];
    }
    if (best < 0) return { rows: [], columns: {}, skipped: 0 };
    col.addr = best;
    // the first text column before or after it is the name, the next one the comment
    const texts = Array.from({ length: width }, (_, j) => j).filter((j) => j !== best && table.some((r) => String(r[j] ?? '').trim() && !isAddr(String(r[j]))));
    if (texts[0] != null) col.name = texts[0];
    if (texts[1] != null) col.comment = texts[1];
    if (table[0] && !isAddr(String(table[0][best] ?? ''))) first = 1; // an unrecognised header
  }
  const cell = (r: string[], k: keyof AssignList['columns']) => (col[k] == null ? '' : String(r[col[k]!] ?? '').trim());
  const rows: AssignRow[] = [];
  let skipped = 0;
  table.slice(first).forEach((r, i) => {
    const addr = cell(r, 'addr').replace(/^"|"$/g, '');
    const decl = DECL.exec(addr);
    const a = decl ? decl[2] : addr;
    const name = decl ? decl[1] : cell(r, 'name'), comment = decl ? (decl[3] ?? decl[4] ?? '') : cell(r, 'comment');
    if (!a && !name) return;
    const parsed = parsePlcAddress(a);
    if (a && !parsed && isAddr(a)) return void skipped++;
    rows.push({ line: first + i + 1, addr: a, parsed, name, comment, card: cell(r, 'card') });
  });
  const columns: AssignList['columns'] = {};
  for (const k of Object.keys(col) as (keyof AssignList['columns'])[]) columns[k] = first ? String(table[0][col[k]!] ?? '') : `#${col[k]! + 1}`;
  return { rows, columns, skipped };
}

export interface AssignChange {
  it: Placed;
  card: string;
  /** how the row was found: by the channel's symbol name / description, or by its address */
  by: 'name' | 'addr';
  line: number;
  old: { addr: string; sym: string; desc: string };
  addr: string;
  sym: string;
  desc: string;
}

export interface AssignResult {
  changes: AssignChange[];
  /** rows matched to a channel that already has the same address and name */
  same: number;
  /** rows that fit no channel */
  unmatched: AssignRow[];
  /** problems: an address that does not fit the channel, one address on two channels after the import */
  problems: string[];
}

const addrKey = (p: PlcAddress) => formatPlcAddress(p);
const fits = (c: PlcChannel, p: PlcAddress) => (p.area === 'I') === (c.kind === 'DI' || c.kind === 'AI') && p.word === (c.kind === 'AI' || c.kind === 'AQ');

/** Matches the rows of an assignment list to the PLC channels of the project. */
export function matchAssignmentList(A: Analysis, list: AssignList): AssignResult {
  const ch = plcChannels(A).filter((c) => A.mains.get(c.key)?.[0]?.s.type !== 'gctl');
  const cardOk = (c: PlcChannel, card: string) => {
    if (!card) return true;
    const want = fold(card.replace(/^[-=+]+/, '')), name = fold(keyName(A, c.key));
    return name === want || name.endsWith(' ' + want);
  };
  const tag = (s: string) => plcTagName(s).toLowerCase();
  const claimed = new Map<string, AssignRow>(); // channel id → row
  const result: AssignResult = { changes: [], same: 0, unmatched: [], problems: [] };
  const target = new Map<string, { c: PlcChannel; row: AssignRow; by: 'name' | 'addr' }>();
  const rest: AssignRow[] = [];
  // 1. by name: the channel's symbol name, else its description (only when one channel has it)
  for (const row of list.rows) {
    const n = row.name && tag(row.name);
    let hit: PlcChannel | undefined;
    if (n) {
      const free = ch.filter((c) => !claimed.has(c.it.s.id) && cardOk(c, row.card));
      hit = free.find((c) => c.it.s.props.sym && tag(c.it.s.props.sym) === n);
      if (!hit) {
        const byDesc = free.filter((c) => c.it.s.desc && tag(c.it.s.desc) === n);
        if (byDesc.length === 1) hit = byDesc[0];
      }
    }
    if (hit) {
      claimed.set(hit.it.s.id, row);
      target.set(hit.it.s.id, { c: hit, row, by: 'name' });
    } else rest.push(row);
  }
  // 2. by address: the channel that has the row's address now
  for (const row of rest) {
    const hit = row.parsed && ch.find((c) => !claimed.has(c.it.s.id) && c.parsed && addrKey(c.parsed) === addrKey(row.parsed!) && cardOk(c, row.card));
    if (hit) {
      claimed.set(hit.it.s.id, row);
      target.set(hit.it.s.id, { c: hit, row, by: 'addr' });
    } else result.unmatched.push(row);
  }
  const final = new Map<string, PlcChannel[]>();
  for (const c of ch) {
    const t = target.get(c.it.s.id);
    let addr = c.parsed ? formatPlcAddress(c.parsed) : c.addr;
    if (t) {
      const { row, by } = t;
      if (row.parsed && !fits(c, row.parsed)) {
        result.problems.push($t('Satır {0}: {1} adresi {2} kanalına uymuyor ({3}, /{4})', row.line, row.addr, $t(PLC_KIND_NAME[c.kind]).toLowerCase(), keyName(A, c.key), loc(c.it)));
      } else if (row.parsed) addr = formatPlcAddress(row.parsed);
      const old = { addr: c.addr, sym: c.it.s.props.sym ?? '', desc: c.it.s.desc };
      const sym = row.name || old.sym, desc = old.desc || row.comment;
      if (addr !== old.addr || sym !== old.sym || desc !== old.desc)
        result.changes.push({ it: c.it, card: keyName(A, c.key), by, line: row.line, old, addr, sym, desc });
      else result.same++;
    }
    const p = parsePlcAddress(addr);
    if (p) (final.get(addrKey(p)) ?? final.set(addrKey(p), []).get(addrKey(p))!).push(c);
  }
  for (const [a, list2] of final)
    if (list2.length > 1 && list2.some((c) => target.has(c.it.s.id)))
      result.problems.push($t('{0} adresi içe aktarmadan sonra {1} kanalda: {2}', a, list2.length, list2.map((c) => `${keyName(A, c.key)} /${loc(c.it)}`).join(', ')));
  return result;
}

/** Writes the matched addresses, symbol names and descriptions into the channels. */
export function applyAssignment(changes: AssignChange[]): void {
  for (const c of changes) {
    c.it.s.props.addr = c.addr;
    if (c.sym) c.it.s.props.sym = c.sym;
    else delete c.it.s.props.sym;
    c.it.s.desc = c.desc;
  }
}
